import type { ErrorCallback, LogLevel } from '../logging/logger';
import { SDK_VERSION } from '../version';

export type DeeplinkCallback = (payload: Record<string, unknown>) => void;

/** An unrecognised level used to rank above every message, silencing even
 *  errors — the opposite of what someone raising the level wants. */
const LOG_LEVELS = new Set<LogLevel>(['info', 'warn', 'error']);

const DEFAULT_BASE_URL = 'https://sdk.sqd.link';
const API_PATH = '/api/v1/sdk';

/** @deprecated The messages inbox was removed; in-app messages replace it. */
export interface MessagesTheme {
  mode?: 'auto' | 'light' | 'dark';
  position?: 'center' | 'right';
  title?: string;
  accentColor?: string;
  backgroundColor?: string;
  textColor?: string;
  mutedTextColor?: string;
  borderRadius?: string;
  fontFamily?: string;
  backdropColor?: string;
  zIndex?: number;
}

export interface GrovsConfig {
  apiKey: string;
  testEnvironment?: boolean;
  /** Custom API domain for self-hosted backends. The SDK appends the API path. */
  baseURL?: string;
  /** Opt-in cross-subdomain cookie scope (spec T11). Host-only when absent. */
  cookieDomain?: string;
  /** SPA route changes fire screen views automatically. Defaults to true,
   *  matching iOS. */
  autoTrackScreenViews?: boolean;
  /**
   * When true, nothing is persisted or transmitted until grantConsent() is
   * called; events accumulate in memory meanwhile. Defaults to false, which
   * preserves v1 behaviour — an integrator who upgrades without reading the
   * changelog must not silently lose data.
   */
  requireConsent?: boolean;
  /** Your app's version, reported with the device fingerprint. Defaults to
   *  the SDK version, since a web page has no build number of its own. */
  appVersion?: string;
  debugLevel?: LogLevel;
  /** @deprecated Accepted and ignored: the messages inbox was removed; in-app messages replace it. */
  messagesTheme?: MessagesTheme;
  onDeeplink?: DeeplinkCallback;
  onError?: ErrorCallback;
  /** Shows the smart banner configured in the dashboard on mobile browsers. Default true. */
  smartBanner?: boolean;
}

export interface ResolvedConfig {
  apiKey: string;
  testEnvironment: boolean;
  endpoint: string;
  cookieDomain?: string;
  autoTrackScreenViews: boolean;
  requireConsent: boolean;
  appVersion: string;
  /** The version the site passed, or empty: in-app version conditions fail when the site names none. */
  inAppVersion: string;
  debugLevel: LogLevel;
  onDeeplink: DeeplinkCallback | null;
  onError: ErrorCallback | null;
  smartBanner: boolean;
}

export function resolveConfig(input: GrovsConfig): ResolvedConfig {
  const apiKey = input.apiKey?.trim() ?? '';
  if (!apiKey) {
    throw new Error(
      'Grovs — API key is required. Use the value from the web console at https://app.grovs.io.',
    );
  }

  const base = (input.baseURL ?? DEFAULT_BASE_URL).replace(/\/+$/, '');

  const resolved: ResolvedConfig = {
    apiKey,
    testEnvironment: input.testEnvironment ?? false,
    endpoint: `${base}${API_PATH}`,
    autoTrackScreenViews: input.autoTrackScreenViews ?? true,
    requireConsent: input.requireConsent ?? false,
    appVersion: input.appVersion?.trim() || SDK_VERSION,
    inAppVersion: typeof input.appVersion === 'string' ? input.appVersion.trim() : '',
    debugLevel: LOG_LEVELS.has(input.debugLevel ?? 'error') ? (input.debugLevel ?? 'error') : 'error',
    onDeeplink: input.onDeeplink ?? null,
    onError: input.onError ?? null,
    smartBanner: input.smartBanner ?? true,
  };
  if (input.cookieDomain) resolved.cookieDomain = input.cookieDomain;
  return resolved;
}
